import React, { useEffect, useMemo, useState } from "react";
import api from "../api/client";
import { usePublishScreenData } from "../utils/aiScreenContext";
import { useSort, sortRows, SortableTh } from "../components/SortableTh";
import { formatDateTime } from "../utils/datetime";

const PRIORITY_RANK: Record<string, number> = { low: 0, medium: 1, high: 2, urgent: 3 };

type Channel = {
  id: number;
  customer_id: number;
  kind: "slack" | "teams";
  external_id: string;
  name: string | null;
  default_priority: string;
  is_active: boolean;
  has_signing_secret: boolean;
  has_bot_token: boolean;
  last_inbound_at: string | null;
  created_at: string;
};

type Customer = { id: number; name: string };
/** A customer's connected Microsoft 365 tenant. Teams identifies an inbound
 *  message by tenant, and if M365 is already connected AeroGauge knows that
 *  GUID - so asking an operator to go and find it in Entra was busywork over
 *  a value already in the database. */
type Tenant = { customer_id: number; tenant_id: string; display_name: string | null };

/** Per-kind vocabulary. Read off the handlers in routers/inbound_chat.py,
 *  not guessed: Slack matches on ``team_id`` and Teams on
 *  ``channelData.tenant.id``. A single "External ID" label meant two
 *  unrelated things depending on a dropdown. */
const KIND_HELP: Record<
  "slack" | "teams",
  { idLabel: string; idHelp: string; idPlaceholder: string; secretLabel: string; secretHelp: string; steps: string[]; note?: string }
> = {
  slack: {
    idLabel: "Slack workspace ID",
    idHelp:
      "Starts with T, e.g. T01234ABCD. Slack sends it as team_id. Find it in Slack → About this workspace, or in the URL of the admin pages.",
    idPlaceholder: "T01234ABCD",
    secretLabel: "Signing secret",
    secretHelp:
      "Slack app → Basic Information → App Credentials → Signing Secret. AeroGauge verifies every request against it.",
    steps: [
      "At api.slack.com/apps open your app (or create one) and go to Slash Commands.",
      "Create a command such as /ticket and set its Request URL to this URL:",
      "Open Basic Information and copy the Signing Secret.",
      "Fill in the workspace ID and that secret below.",
    ],
  },
  teams: {
    idLabel: "Microsoft 365 tenant ID",
    idHelp:
      "The tenant GUID. Entra admin centre → Overview → Tenant ID. Teams sends it on every message as channelData.tenant.id.",
    idPlaceholder: "00000000-0000-0000-0000-000000000000",
    secretLabel: "Security token",
    secretHelp:
      "Teams shows this ONCE, when you finish creating the Outgoing Webhook. Copy it then — it cannot be shown again.",
    steps: [
      "In Teams open the team, press ... beside its name and choose Manage team.",
      "Go to the Apps tab and press Create an outgoing webhook at the bottom.",
      "Name it AeroGauge and set the callback URL to this URL:",
      "Press Create. Copy the security token it shows you, then fill both fields below.",
    ],
    note:
      "Teams identifies the sender by TENANT, not by channel, so one Microsoft 365 tenant maps to one customer here.",
  },
};

function CopyRow({ label, value, hint }: { label: string; value: string; hint: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex flex-wrap items-center gap-2 py-1.5">
      <span className="text-xs font-medium text-gray-700 dark:text-gray-200 w-28 shrink-0">
        {label}
      </span>
      <code className="flex-1 min-w-0 truncate bg-gray-50 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-700 rounded px-2 py-1 text-[11px] text-gray-800 dark:text-gray-200">
        {value}
      </code>
      <button
        onClick={() => {
          navigator.clipboard.writeText(value).then(
            () => {
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            },
            () => {},
          );
        }}
        className="px-2.5 py-1 text-xs rounded-lg bg-brand text-white hover:opacity-90 shrink-0"
      >
        {copied ? "Copied" : "Copy"}
      </button>
      <span className="basis-full text-[11px] text-gray-500 dark:text-gray-400 pl-28">
        {hint}
      </span>
    </div>
  );
}

export default function InboundChannels({ embedded = false }: { embedded?: boolean }) {
  const [channels, setChannels] = useState<Channel[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  // refresh() toggles this, but nothing in the page body reads `busy` (the
  // modal has its own busy state), so we keep only the setter.
  const [, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Channel | null>(null);
  const [showModal, setShowModal] = useState(false);
  const chSort = useSort("customer", "asc");

  // Publish the visible rows so the global AI Assist can answer
  // "what's on my screen". Projected explicitly: this payload goes
  // into a model prompt.
  usePublishScreenData(
    "Inbound email channels",
    useMemo(
      () =>
        channels.map((c: any) => ({
          customer: c.customer_name ?? null,
          address: c.address ?? c.mailbox ?? null,
          kind: c.kind ?? null,
          enabled: c.enabled ?? null,
          last_polled_at: c.last_polled_at ?? null,
          last_error: c.last_error ?? null,
          // Status only - the row also carries mailbox credentials.
        })),
      [channels],
    ),
    undefined,
  );
  // Which vendor the on-screen instructions describe. Teams first: it has
  // the longer path and it is the one people get stuck on.
  const [guideKind, setGuideKind] = useState<"slack" | "teams">("teams");
  // The URL Slack/Teams must call comes from the APPLIANCE, not the browser.
  // An admin on https://localhost or an IP would otherwise copy an address
  // the vendor cannot resolve, paste it, and get silence.
  const [endpoints, setEndpoints] = useState<{
    slack: string;
    teams: string;
    configured: boolean;
  } | null>(null);

  const refresh = async () => {
    setBusy(true);
    try {
      const [c, custs, tens] = await Promise.all([
        api.get("/api/v1/inbound/channels"),
        api.get("/api/v1/customers"),
        api.get("/api/v1/m365/tenants").catch(() => ({ data: [] })),
      ]);
      api
        .get("/api/v1/inbound/channels/endpoints")
        .then((r) => setEndpoints(r.data))
        .catch(() => setEndpoints(null));
      setChannels(c.data);
      setCustomers(custs.data);
      setTenants(tens.data || []);
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const customerName = (id: number) =>
    customers.find((c) => c.id === id)?.name || `#${id}`;

  const remove = async (id: number) => {
    if (!window.confirm("Delete this inbound channel?")) return;
    await api.delete(`/api/v1/inbound/channels/${id}`);
    refresh();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className={embedded ? "hidden" : ""}>
          <h1 className="text-2xl font-bold">Inbound chat channels</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            <strong>Chat &rarr; AeroGauge.</strong> Someone types in Slack or Teams and a
            ticket appears here. Slack uses a slash command; Teams uses an{" "}
            <em>Outgoing Webhook</em>. Per-channel signing secrets are encrypted
            (AES-256-GCM) before storage.
          </p>
          {/* The two chat features are named after opposite ends of the same
              word, and it is genuinely easy to set up the wrong one: Teams
              calls the thing that sends OUT of Teams an "Outgoing Webhook",
              and the thing that receives INTO Teams a workflow that "receives
              a webhook request". Point at each other rather than letting
              someone discover it by getting no alerts. */}
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Going the other way — AeroGauge alerts posted into a Slack or Teams channel — is{" "}
            <a
              href="/integrations/webhooks"
              className="text-brand dark:text-brand-light hover:underline"
            >
              Integrations &rarr; Alert destinations
            </a>
            .
          </p>
        </div>
        <button
          onClick={() => {
            setEditing(null);
            setShowModal(true);
          }}
          className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium"
        >
          + Add channel
        </button>
      </div>

      {/* THE INSTRUCTIONS GO ON THE SCREEN. They used to live inside the
          "+ Add channel" modal, so the page you land on showed a URL, a
          table, and no account of what to do with either. And the URL now
          sits INSIDE the step that says to paste it, rather than in its own
          box above the thing it belongs to. */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div>
            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              How to wire this up
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              You create the webhook in the chat tool and give it the AeroGauge URL below.
              Nothing gets pasted the other way.
            </p>
          </div>
          <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-600 p-0.5 bg-gray-50 dark:bg-gray-900/50">
            {(["teams", "slack"] as const).map((k) => (
              <button
                key={k}
                onClick={() => setGuideKind(k)}
                className={`px-3 py-1.5 text-xs rounded-md ${
                  guideKind === k
                    ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm"
                    : "text-gray-500 dark:text-gray-400"
                }`}
              >
                {k === "teams" ? "Microsoft Teams" : "Slack"}
              </button>
            ))}
          </div>
        </div>

        <ol className="space-y-2.5">
          {KIND_HELP[guideKind].steps.map((s, i) => {
            const wantsUrl = /url/i.test(s);
            return (
              <li key={i} className="flex gap-2.5">
                <span className="shrink-0 w-5 h-5 mt-0.5 rounded-full bg-brand text-white text-[11px] flex items-center justify-center">
                  {i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-gray-700 dark:text-gray-200">{s}</div>
                  {wantsUrl && (
                    <CopyRow
                      label="This URL"
                      value={
                        endpoints
                          ? endpoints[guideKind]
                          : `${window.location.origin}/api/v1/inbound/${guideKind}`
                      }
                      hint={
                        endpoints && !endpoints.configured
                          ? "WARNING: this appliance has no public address configured, so Slack and Teams will not be able to reach it."
                          : "AeroGauge listens here. It must be reachable from the internet."
                      }
                    />
                  )}
                </div>
              </li>
            );
          })}
          <li className="flex gap-2.5">
            <span className="shrink-0 w-5 h-5 mt-0.5 rounded-full bg-brand text-white text-[11px] flex items-center justify-center">
              {KIND_HELP[guideKind].steps.length + 1}
            </span>
            <div className="text-sm text-gray-700 dark:text-gray-200">
              Come back here, press <strong>+ Add channel</strong>, and tell AeroGauge which
              customer that workspace belongs to.
            </div>
          </li>
        </ol>
        {/* HOW TO USE IT, not just how to wire it. The screen explained
            setup and then stopped, so somebody who finished the setup had no
            idea what to do next - reported as "I set it up and I don't know
            how to use it". */}
        <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
          <div className="text-xs font-medium text-gray-700 dark:text-gray-200 mb-1.5">
            Then, to raise a ticket
          </div>
          {guideKind === "teams" ? (
            <p className="text-sm text-gray-700 dark:text-gray-200">
              In any channel of that team, <strong>@mention the webhook by the name you
              gave it</strong> and type the problem after it &mdash; for example{" "}
              <code className="bg-gray-100 dark:bg-gray-900/60 px-1.5 py-0.5 rounded text-xs">
                @AeroGauge printer in reception is offline
              </code>
              . AeroGauge opens a ticket on the mapped customer and replies in the thread
              with its number.
            </p>
          ) : (
            <p className="text-sm text-gray-700 dark:text-gray-200">
              In any channel, type your slash command followed by the problem &mdash; for
              example{" "}
              <code className="bg-gray-100 dark:bg-gray-900/60 px-1.5 py-0.5 rounded text-xs">
                /ticket printer in reception is offline
              </code>
              . AeroGauge opens a ticket on the mapped customer and replies with its number.
            </p>
          )}
          <p className="mt-1.5 text-[11px] text-gray-500 dark:text-gray-400">
            {guideKind === "teams"
              ? "A Teams outgoing webhook belongs to the whole team, not one channel - it works wherever you @mention it, and the ticket is the same either way."
              : "The message text becomes the ticket title and description."}
          </p>
        </div>

                {KIND_HELP[guideKind].note && (
          <p className="mt-3 text-[11px] text-amber-700 dark:text-amber-300">
            {KIND_HELP[guideKind].note}
          </p>
        )}
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 px-3 py-2 rounded text-sm">
          {error}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded border dark:border-gray-700 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">
            <tr>
              <SortableTh label="Customer" sortKey="customer" sort={chSort.sort} onSort={chSort.toggle} hide="hidden md:table-cell" />
              <SortableTh label="Kind" sortKey="kind" sort={chSort.sort} onSort={chSort.toggle} hide="hidden sm:table-cell" />
              <SortableTh label="Name" sortKey="name" sort={chSort.sort} onSort={chSort.toggle} />
              <SortableTh label="External ID" sortKey="external_id" sort={chSort.sort} onSort={chSort.toggle} hide="hidden xl:table-cell" />
              <SortableTh label="Default priority" sortKey="default_priority" sort={chSort.sort} onSort={chSort.toggle} hide="hidden lg:table-cell" />
              <SortableTh label="Active" sortKey="is_active" sort={chSort.sort} onSort={chSort.toggle} />
              <SortableTh label="Last inbound" sortKey="last_inbound_at" sort={chSort.sort} onSort={chSort.toggle} hide="hidden lg:table-cell" />
              <th className="text-right py-2 px-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {channels.length === 0 ? (
              <tr>
                <td colSpan={8} className="py-8 text-center text-gray-400 dark:text-gray-500">
                  No inbound channels yet. Add one to start accepting Slack/Teams tickets.
                </td>
              </tr>
            ) : (
              sortRows(channels, chSort.sort, (c, key) => {
                switch (key) {
                  case "kind": return (c.kind || "").toLowerCase();
                  case "name": return (c.name || "").toLowerCase();
                  case "external_id": return (c.external_id || "").toLowerCase();
                  case "default_priority": return PRIORITY_RANK[c.default_priority] ?? -1;
                  case "is_active": return c.is_active ? 1 : 0;
                  case "last_inbound_at": return c.last_inbound_at ? Date.parse(c.last_inbound_at) : 0;
                  default: return customerName(c.customer_id).toLowerCase();
                }
              }).map((c) => (
                <tr key={c.id} className="border-b dark:border-gray-700 last:border-0">
                  <td className="py-2 px-3 hidden md:table-cell">{customerName(c.customer_id)}</td>
                  <td className="py-2 px-3 hidden sm:table-cell">
                    <span
                      className={`text-xs px-2 py-0.5 rounded font-mono ${
                        c.kind === "slack"
                          ? "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300"
                          : "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300"
                      }`}
                    >
                      {c.kind}
                    </span>
                  </td>
                  <td className="py-2 px-3">{c.name || "—"}</td>
                  <td className="py-2 px-3 font-mono text-xs hidden xl:table-cell">{c.external_id}</td>
                  <td className="py-2 px-3 capitalize hidden lg:table-cell">{c.default_priority}</td>
                  <td className="py-2 px-3">{c.is_active ? "✓" : ""}</td>
                  <td className="py-2 px-3 text-xs text-gray-500 dark:text-gray-400 hidden lg:table-cell">
                    {c.last_inbound_at
                      ? formatDateTime(c.last_inbound_at)
                      : "never"}
                  </td>
                  <td className="py-2 px-3 text-right space-x-2">
                    <button
                      onClick={() => {
                        setEditing(c);
                        setShowModal(true);
                      }}
                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => remove(c.id)}
                      className="text-xs text-red-600 dark:text-red-400 hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <ChannelModal
          editing={editing}
          customers={customers}
          tenants={tenants}
          onClose={() => setShowModal(false)}
          onSaved={() => {
            setShowModal(false);
            refresh();
          }}
        />
      )}
    </div>
  );
}

function ChannelModal({
  editing,
  customers,
  tenants,
  onClose,
  onSaved,
}: {
  editing: Channel | null;
  customers: Customer[];
  tenants: Tenant[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [customerId, setCustomerId] = useState<number | "">(editing?.customer_id || "");
  const [kind, setKind] = useState<"slack" | "teams">(editing?.kind || "slack");
  const [externalId, setExternalId] = useState(editing?.external_id || "");
  const [name, setName] = useState(editing?.name || "");
  const [signingSecret, setSigningSecret] = useState("");
  const [botToken, setBotToken] = useState("");
  const [defaultPriority, setDefaultPriority] = useState(
    editing?.default_priority || "medium"
  );
  const [isActive, setIsActive] = useState(editing?.is_active ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Teams matches on the Microsoft 365 tenant, and for a customer whose M365
  // is already connected that GUID is sitting in the database. Fill it in
  // rather than sending someone to the Entra portal for a value we hold.
  const knownTenant = tenants.find((x) => x.customer_id === customerId)?.tenant_id || "";
  const autoFilled = kind === "teams" && !!knownTenant && externalId === knownTenant;
  useEffect(() => {
    if (editing || kind !== "teams" || !knownTenant) return;
    setExternalId((cur) => (cur ? cur : knownTenant));
  }, [editing, kind, knownTenant]);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const body: any = {
        kind,
        external_id: externalId,
        name: name || null,
        default_priority: defaultPriority,
        is_active: isActive,
      };
      if (signingSecret) body.signing_secret = signingSecret;
      if (botToken) body.bot_token = botToken;
      if (editing) {
        await api.put(`/api/v1/inbound/channels/${editing.id}`, body);
      } else {
        if (!customerId) {
          setError("Customer required");
          setBusy(false);
          return;
        }
        if (!signingSecret) {
          setError("Signing secret required on create");
          setBusy(false);
          return;
        }
        await api.post("/api/v1/inbound/channels", {
          customer_id: customerId,
          ...body,
        });
      }
      onSaved();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-5 w-full max-w-lg space-y-3 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold">
          {editing ? "Edit channel" : "Add inbound channel"}
        </h3>
        {!editing && (
          <>
            <Field label="Customer">
              <select
                value={customerId}
                onChange={(e) => setCustomerId(Number(e.target.value))}
                className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1.5 text-sm"
              >
                <option value="">Select…</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Kind">
              <select
                value={kind}
                onChange={(e) => setKind(e.target.value as any)}
                className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1.5 text-sm"
              >
                <option value="slack">Slack</option>
                <option value="teams">Teams</option>
              </select>
            </Field>
          </>
        )}
        {/* You cannot fill this form without having created the webhook on
            the vendor side first, and the form never said so. The steps come
            before the fields for the same reason they do on the outbound
            screen. */}
        {!editing && (
          <div className="rounded-lg bg-gray-50 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-700 px-3 py-2.5">
            <div className="text-xs font-medium text-gray-700 dark:text-gray-200 mb-1.5">
              Do this in {kind === "slack" ? "Slack" : "Teams"} first
            </div>
            <ol className="space-y-1">
              {KIND_HELP[kind].steps.map((s, i) => (
                <li key={i} className="text-xs text-gray-600 dark:text-gray-300 flex gap-2">
                  <span className="shrink-0 w-4 h-4 mt-0.5 rounded-full bg-gray-200 dark:bg-gray-700 text-[10px] flex items-center justify-center text-gray-700 dark:text-gray-200">
                    {i + 1}
                  </span>
                  <span>{s}</span>
                </li>
              ))}
            </ol>
            {KIND_HELP[kind].note && (
              <p className="mt-2 text-[11px] text-amber-700 dark:text-amber-300">
                {KIND_HELP[kind].note}
              </p>
            )}
          </div>
        )}
        <Field label={KIND_HELP[kind].idLabel}>
          <input
            value={externalId}
            onChange={(e) => setExternalId(e.target.value)}
            placeholder={KIND_HELP[kind].idPlaceholder}
            className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1.5 text-sm font-mono"
            disabled={!!editing}
          />
          {autoFilled ? (
            <p className="mt-1 text-[11px] text-emerald-700 dark:text-emerald-400">
              Filled in from this customer&rsquo;s connected Microsoft 365 tenant. Change it only
              if their Teams lives in a different tenant.
            </p>
          ) : (
            <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
              {KIND_HELP[kind].idHelp}
              {kind === "teams" && !knownTenant && customerId
                ? " This customer has no Microsoft 365 tenant connected, or AeroGauge would have filled it in."
                : ""}
            </p>
          )}
        </Field>
        <Field label="Display name">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1.5 text-sm"
            placeholder="Acme Slack workspace"
          />
        </Field>
        <Field
          label={`${KIND_HELP[kind].secretLabel}${
            editing?.has_signing_secret ? " (stored — blank keeps existing)" : ""
          }`}
        >
          <input
            type="password"
            value={signingSecret}
            onChange={(e) => setSigningSecret(e.target.value)}
            className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1.5 text-sm font-mono"
            placeholder={kind === "slack" ? "from Basic Information" : "shown once, at creation"}
          />
          <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
            {KIND_HELP[kind].secretHelp}
          </p>
        </Field>
        {kind === "slack" && (
          <Field
            label={`Bot token ${
              editing?.has_bot_token ? "(stored — blank keeps; empty to clear)" : "(optional)"
            }`}
          >
            <input
              type="password"
              value={botToken}
              onChange={(e) => setBotToken(e.target.value)}
              className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1.5 text-sm font-mono"
              placeholder="xoxb-..."
            />
          </Field>
        )}
        <div className="flex gap-3">
          <Field label="Default priority">
            <select
              value={defaultPriority}
              onChange={(e) => setDefaultPriority(e.target.value)}
              className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1.5 text-sm"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </Field>
          <label className="flex items-center gap-2 text-sm pt-6">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            Active
          </label>
        </div>

        {error && (
          <div className="text-xs bg-red-50 dark:bg-red-900/40 text-red-800 dark:text-red-300 p-2 rounded">{error}</div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 border dark:border-gray-600 rounded text-sm hover:bg-gray-50 dark:hover:bg-gray-700/40"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={busy}
            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium disabled:opacity-50"
          >
            {editing ? "Save" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
      {children}
    </div>
  );
}
